import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';

const API_URL = process.env.REACT_APP_API_URL || '';

interface YearPickerProps {
  value: number | undefined;
  onChange: (year: number) => void;
  disabled?: boolean;
}

const YearPicker: React.FC<YearPickerProps> = ({ value, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const startYear = 1900;
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);

  const handleYearSelect = (year: number) => {
    onChange(year);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value || ''}
        readOnly
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm cursor-pointer bg-white"
        placeholder="Select year"
        disabled={disabled}
      />
      {!disabled && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      
      {isOpen && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            <div className="py-1">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  className={`block w-full text-left px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 ${
                    value === year ? 'bg-blue-100 text-blue-700' : 'text-gray-700'
                  }`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Options arrays (matching the modal)
const coverTypes = [
  'Comprehensive',
  'Third Party Fire and Theft',
  'Third Party Property Damage',
  'Compulsory Third Party'
];

const noClaimBonusOptions = [
  'None', '12 Months (1 Year)', '24 Months (2 Year)', '36 Months (3 Year)',
  '48 Months (4 Year)', '60 Months (5 Year)', '72 Months (6 Year)',
  '84 Months (7 Year)', '96 Months (8 Year)', '108 Months (9 Year)', '120 Months (10 Year)'
];

// Currency formatting helpers
const formatCurrencyInput = (value: string): string => {
  if (!value) return '';
  const numericValue = value.replace(/[^\d.]/g, '');
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  if (decimalPart.length > 2) decimalPart = decimalPart.substring(0, 2);
  if (wholePart) wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
};

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/,/g, ''));
};

const AddTrailerCaravanPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPolicy = searchParams.get('fromPolicy') === 'true';
  const { quoteId } = useParams<{ quoteId: string }>();
  const clientId = location.state?.clientId || new URLSearchParams(location.search).get('clientId');
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  const policyId = new URLSearchParams(location.search).get('policyId'); // ✅ ADD THIS
  
  // Determine if we're in master quote page mode (has returnTo URL) or create quote mode
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddTrailerCaravanPage mounted:', { 
    quoteId, 
    clientId, 
    returnTo, 
    isMasterQuoteMode,
    policyId, // ✅ ADD THIS
    fromPolicy // ✅ ADD THIS
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [localSaveMessage, setLocalSaveMessage] = useState('');
  
  // Form state
  const [trailerType, setTrailerType] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [coverType, setCoverType] = useState('');
  const [insuredValue, setInsuredValue] = useState('');
  const [useRetailValue, setUseRetailValue] = useState(false);
  const [premium, setPremium] = useState('');
  const [excess, setExcess] = useState('');
  const [noClaimBonus, setNoClaimBonus] = useState('');
  
  const [currencyDisplay, setCurrencyDisplay] = useState({
    insuredValue: '',
    premium: '',
    excess: '',
  });
  
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  // Trailer types
  const trailerTypes = [
    'Trailer', 'Caravan', 'Camper Trailer', 'Horse Trailer', 'Boat Trailer',
    'Box Trailer', 'Flatbed Trailer', 'Enclosed Trailer', 'Utility Trailer', 'Other'
  ];
  
  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);
  
  // Fetch existing trailer/caravan if editing
useEffect(() => {
  const editId = new URLSearchParams(location.search).get('editId');
  if (editId && quoteId) {
    setEditingId(editId);
    fetchTrailerForEdit(editId);
  }
}, [quoteId, location.search, policyId, fromPolicy]); // ✅ Add policyId and fromPolicy
  
  const fetchTrailerForEdit = async (id: string) => {
  try {
    // ✅ Check localStorage first if it's a temp ID
    if (id.startsWith('temp-')) {
      let storageKey: string;
      
      if (policyId && fromPolicy) {
        storageKey = `policy_${policyId}_trailer`;
      } else {
        storageKey = `client_${clientId}_trailers_draft`;
      }
      
      const storedItems = JSON.parse(localStorage.getItem(storageKey) || '[]');
      const foundItem = storedItems.find((item: any) => item.id === id);
      
      if (foundItem) {
        console.log('📦 Found trailer/caravan in localStorage:', foundItem);
        // Populate form with found item
        setTrailerType(foundItem.vehicleType || foundItem.vehicle_type || foundItem.trailer_type || '');
        setRegistrationNumber(foundItem.registrationNumber || foundItem.registration_number || '');
        setMake(foundItem.make || '');
        setModel(foundItem.model || '');
        setYear(foundItem.year?.toString() || '');
        setCoverType(foundItem.coverType || foundItem.cover_type || '');
        setInsuredValue((foundItem.insuredValue || foundItem.insured_value || 0).toString());
        setUseRetailValue(!!(foundItem.useRetailValue || foundItem.use_retail_value));
        setPremium((foundItem.premium || 0).toString());
        setExcess((foundItem.excess || 0).toString());
        
        const mapBackendToFrontendNoClaimsBonus = (backendValue: string): string => {
          const mapping: { [key: string]: string } = {
            "None": "None",
            "1 Year": "12 Months (1 Year)",
            "2 Year": "24 Months (2 Year)",
            "3 Year": "36 Months (3 Year)",
            "4 Year": "48 Months (4 Year)",
            "5 Year Plus": "60 Months (5 Year)"
          };
          return mapping[backendValue] || "None";
        };
        
        const backendNoClaimsBonus = foundItem.noClaimsBonus || foundItem.no_claims_bonus || '';
        setNoClaimBonus(mapBackendToFrontendNoClaimsBonus(backendNoClaimsBonus));
        
        setCurrencyDisplay({
          insuredValue: formatCurrencyInput((foundItem.insuredValue || foundItem.insured_value || 0).toString()),
          premium: formatCurrencyInput((foundItem.premium || 0).toString()),
          excess: formatCurrencyInput((foundItem.excess || 0).toString()),
        });
        return;
      }
    }
    
    // Not found in localStorage or not a temp ID - fetch from API
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/trailer-caravans/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const trailer = response.data.data || response.data;
    
        setTrailerType(trailer.vehicle_type || trailer.vehicleType || trailer.trailer_type || '');
    setRegistrationNumber(trailer.registration_number || trailer.registrationNumber || '');
    setMake(trailer.make || '');
    setModel(trailer.model || '');
    setYear(trailer.year?.toString() || '');
    setCoverType(trailer.cover_type || trailer.coverType || '');
    
    // ✅ Parse and format the numeric values
    const rawInsuredValue = parseFloat((trailer.insured_value || trailer.insuredValue || 0).toString()) || 0;
    const rawPremium = parseFloat((trailer.premium || 0).toString()) || 0;
    const rawExcess = parseFloat((trailer.excess || 0).toString()) || 0;
    
    setInsuredValue(rawInsuredValue.toString());
    setUseRetailValue(!!(trailer.use_retail_value || trailer.useRetailValue));
    setPremium(rawPremium.toString());
    setExcess(rawExcess.toString());
    
    setCurrencyDisplay({
      insuredValue: formatCurrencyInput(rawInsuredValue.toFixed(2)),
      premium: formatCurrencyInput(rawPremium.toFixed(2)),
      excess: formatCurrencyInput(rawExcess.toFixed(2)),
    });

    // ✅ Map the no claim bonus from backend format ("4 Year") to frontend format ("48 Months (4 Year)")
    const backendNoClaimsBonus = trailer.no_claims_bonus || trailer.noClaimsBonus || '';
    const mapBackendToFrontendNoClaimsBonus = (backendValue: string): string => {
      const mapping: { [key: string]: string } = {
        "None": "None",
        "1 Year": "12 Months (1 Year)",
        "2 Year": "24 Months (2 Year)",
        "3 Year": "36 Months (3 Year)",
        "4 Year": "48 Months (4 Year)",
        "5 Year Plus": "60 Months (5 Year)"
      };
      return mapping[backendValue] || "None";
    };
    setNoClaimBonus(mapBackendToFrontendNoClaimsBonus(backendNoClaimsBonus));
    
  } catch (error) {
    console.error('Error fetching trailer for edit:', error);
  }
};
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      if (name === 'useRetailValue') setUseRetailValue(checked);
    } else {
      switch (name) {
        case 'trailerType': setTrailerType(value); break;
        case 'registrationNumber': setRegistrationNumber(value); break;
        case 'make': setMake(value); break;
        case 'model': setModel(value); break;
        case 'year': setYear(value); break;
        case 'coverType': setCoverType(value); break;
        case 'insuredValue':
          setCurrencyDisplay(prev => ({ ...prev, insuredValue: formatCurrencyInput(value) }));
          setInsuredValue(parseCurrencyInput(value).toString());
          break;
        case 'premium':
          setCurrencyDisplay(prev => ({ ...prev, premium: formatCurrencyInput(value) }));
          setPremium(parseCurrencyInput(value).toString());
          break;
        case 'excess':
          setCurrencyDisplay(prev => ({ ...prev, excess: formatCurrencyInput(value) }));
          setExcess(parseCurrencyInput(value).toString());
          break;
        case 'noClaimBonus': setNoClaimBonus(value); break;
        default: break;
      }
    }
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!trailerType) newErrors.trailerType = 'Trailer/Caravan Type is required';
    if (!registrationNumber) newErrors.registrationNumber = 'Registration Number is required';
    if (!make) newErrors.make = 'Make is required';
    if (!model) newErrors.model = 'Model is required';
    if (!coverType) newErrors.coverType = 'Cover Type is required';
    if (!insuredValue || parseFloat(insuredValue) <= 0) newErrors.insuredValue = 'Insured Value is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    if (!validateForm()) return;
    
    // Validate clientId
    if (!clientId || isNaN(parseInt(clientId))) {
      setErrors(prev => ({ ...prev, server: 'Valid client ID is required' }));
      return;
    }
    
    setIsSaving(true);
    setLocalSaveMessage('Saving trailer/caravan...');
    
    // Clean the quoteId - use undefined instead of null
    let cleanQuoteId: number | undefined = undefined;
    if (quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId))) {
      cleanQuoteId = parseInt(quoteId);
    }
    
    // Helper function to convert to number
    const toNumber = (value: any): number => {
      if (value === undefined || value === null || value === '') return 0;
      const num = Number(value);
      return isNaN(num) ? 0 : num;
    };
    
    // Map noClaimsBonus to backend expected values
const mapNoClaimsBonusForBackend = (frontendValue: string): string => {
  const mapping: { [key: string]: string } = {
    "None": "None",
    "12 Months (1 Year)": "1 Year",
    "24 Months (2 Year)": "2 Year",
    "36 Months (3 Year)": "3 Year",
    "48 Months (4 Year)": "4 Year",
    "60 Months (5 Year)": "5 Year Plus",
    "72 Months (6 Year)": "5 Year Plus",
    "84 Months (7 Year)": "5 Year Plus",
    "96 Months (8 Year)": "5 Year Plus",
    "108 Months (9 Year)": "5 Year Plus",
    "120 Months (10 Year)": "5 Year Plus"
  };
  return mapping[frontendValue] || "None";
};

const submitData = {
  vehicleType: trailerType,
  registrationNumber: registrationNumber,
  make: make,
  model: model,
  year: year ? parseInt(year) : null,
  coverType: coverType,
  insuredValue: toNumber(insuredValue),
  useRetailValue: useRetailValue,
  premium: toNumber(premium),
  excess: toNumber(excess),
  noClaimsBonus: mapNoClaimsBonusForBackend(noClaimBonus),
  client_id: parseInt(clientId),
  quote_id: cleanQuoteId
};

console.log('📤 Submitting trailer/caravan data (snake_case):', JSON.stringify(submitData, null, 2));
    
    try {
      const token = localStorage.getItem('token');
      
      // If no quote_id, save to localStorage (draft mode)
if (!cleanQuoteId) {
  console.log('💾 No quote ID - saving trailer/caravan to localStorage');
  
  // ✅ Determine the storage key based on context
  let storageKey: string;
  
  // ✅ Only use policy key if we're explicitly coming from policy
  if (policyId && fromPolicy === true) {
    // Coming from Policy Detail page - save with policy ID key
    storageKey = `policy_${policyId}_trailer`;
    console.log(`📂 Saving trailer/caravan with policy key: ${storageKey}`);
  } else {
    // Coming from Quote/Create Quote page - save with client draft key
    storageKey = `client_${clientId}_trailers_draft`;
    console.log(`📂 Saving trailer/caravan with client draft key: ${storageKey}`);
  }
  
  const existingDrafts = JSON.parse(localStorage.getItem(storageKey) || '[]');
  
  // Check if we're editing an existing draft
  let updatedDrafts;
  if (editingId && editingId.startsWith('temp-')) {
    // Update existing draft
    updatedDrafts = existingDrafts.map((item: any) => 
      item.id === editingId 
        ? { 
            ...item, 
            ...submitData, 
            id: editingId,
            isTemp: true,
            status: 'active',
            inception_date: new Date().toISOString().split('T')[0],
            product_type: 'trailer-caravans'
          }
        : item
    );
  } else {
    // Add new draft
    updatedDrafts = [
      ...existingDrafts,
      {
        ...submitData,
        id: `temp-${Date.now()}`,
        isTemp: true,
        status: 'active',
        inception_date: new Date().toISOString().split('T')[0],
        product_type: 'trailer-caravans'
      }
    ];
  }
  
  localStorage.setItem(storageKey, JSON.stringify(updatedDrafts));
  
  setLocalSaveMessage('Trailer/Caravan saved to draft!');
  
  // ✅ Navigate based on context
  setTimeout(() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=trailer&clientId=${clientId}`;
      console.log('📍 Returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Master quote mode - navigate back to master quote page
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=trailer&clientId=${clientId}`;
      console.log('📍 Returning to master quote page:', finalUrl);
      navigate(finalUrl);
    } else {
      // Create quote mode
      navigate(`/create-quote/${clientId}`, { 
        state: { refreshProducts: true, hasDrafts: true }
      });
    }
  }, 1500);

      } else {
  // Has quote_id - save to database
  if (editingId) {
    await axios.put(`${API_URL}/api/trailer-caravans/${editingId}`, submitData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setLocalSaveMessage('Trailer/Caravan updated successfully!');
    
    // ✅ LOG ACTIVITY: Trailer/Caravan Updated
    if (cleanQuoteId) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const performedBy = user?.username || user?.email || 'System';
        
        await axios.post(
          `${API_URL}/api/quote-activity-logs`,
          {
            quote_id: cleanQuoteId,
            action_type: 'product_updated',
            description: `Trailer/Caravan updated: ${make || ''} ${model || ''}`.trim() || 'Trailer/Caravan',
            metadata: {
              productType: 'trailer',
              productId: editingId,
              performedBy: performedBy,
              make: make || '',
              model: model || '',
              registrationNumber: registrationNumber || '',
              year: year || '',
              trailerType: trailerType || '',
              insuredValue: parseFloat(insuredValue) || 0,
              coverType: coverType || '',
              updatedAt: new Date().toISOString()
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Activity logged: trailer_updated');
      } catch (logError) {
        console.error('❌ Failed to log trailer update:', logError);
        // Don't fail the update if logging fails
      }
    }
  } else {
    await axios.post(`${API_URL}/api/trailer-caravans`, submitData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setLocalSaveMessage('Trailer/Caravan added successfully!');
    
    // ✅ LOG ACTIVITY: Trailer/Caravan Added
    if (cleanQuoteId) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const performedBy = user?.username || user?.email || 'System';
        
        await axios.post(
          `${API_URL}/api/quote-activity-logs`,
          {
            quote_id: cleanQuoteId,
            action_type: 'product_added',
            description: `Trailer/Caravan added: ${make || ''} ${model || ''}`.trim() || 'Trailer/Caravan',
            metadata: {
              productType: 'trailer',
              performedBy: performedBy,
              make: make || '',
              model: model || '',
              registrationNumber: registrationNumber || '',
              year: year || '',
              trailerType: trailerType || '',
              insuredValue: parseFloat(insuredValue) || 0,
              coverType: coverType || '',
              addedAt: new Date().toISOString()
            }
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Activity logged: trailer_added');
      } catch (logError) {
        console.error('❌ Failed to log trailer addition:', logError);
        // Don't fail the addition if logging fails
      }
    }
  }

  setIsSaving(false); // ✅ Reset the saving state
  
  // Navigate based on mode
  setTimeout(() => {
    const validClientId = clientId || new URLSearchParams(location.search).get('clientId');

    if (!validClientId) {
      console.error('❌ No clientId available for navigation');
      setLocalSaveMessage('Error: Missing client ID');
      setIsSaving(false);
      return;
    }

    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=trailer&clientId=${validClientId}`;
      console.log('📍 Returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Master quote mode — navigate back to master quote page
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=trailer&clientId=${validClientId}`;
      console.log('📍 Returning to master quote page:', finalUrl);
      navigate(finalUrl);
    } else if (cleanQuoteId) {
      // Create quote mode with quoteId
      navigate(`/quotes/master/${validClientId}/${cleanQuoteId}?refreshProducts=true&productAdded=trailer`);
    } else {
      // Create quote mode
      navigate(`/create-quote/${validClientId}?refreshProducts=true`);
    }
  }, 1500);
}
      
    } catch (error: any) {
  console.error('Error saving trailer/caravan:', error);
  console.error('🔍 Backend error response DATA:', error.response?.data);
  console.error('🔍 Backend error status:', error.response?.status);
  console.error('🔍 Full error:', JSON.stringify(error.response?.data, null, 2));
  
  // Show detailed error message
  const errorMessage = error.response?.data?.message || 
                       error.response?.data?.error || 
                       error.response?.data?.details ||
                       'Failed to save trailer/caravan';
  setLocalSaveMessage(errorMessage);
  setIsSaving(false);
}
  };
  
  if (!user) return <div>Loading...</div>;
  
  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Trailer/Caravan' : 'Add Trailer/Caravan'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-3xl mx-auto">

            <button
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=trailer&clientId=${clientId}`;
      console.log('📍 Back button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=trailer&clientId=${clientId}`;
      console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      // Go back to create quote page
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
>
  ← {fromPolicy ? 'Back to Policy Details' : 'Back to Quote'}
</button>
            
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold mb-6">
                {editingId ? 'Edit Trailer/Caravan Details' : 'Add Trailer/Caravan Details'}
              </h2>
              
              {localSaveMessage && (
                <div className={`mb-4 p-3 rounded ${localSaveMessage.includes('❌') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {localSaveMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Trailer/Caravan Type */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Trailer/Caravan Type</label>
                    <select
                      name="trailerType"
                      value={trailerType}
                      onChange={handleInputChange}
                      className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.trailerType ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">Select Type</option>
                      {trailerTypes.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                    {errors.trailerType && <p className="text-red-500 text-xs mt-1">{errors.trailerType}</p>}
                  </div>
                  
                  {/* Registration Number */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Registration Number</label>
                    <input
                      type="text"
                      name="registrationNumber"
                      value={registrationNumber}
                      onChange={handleInputChange}
                      className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.registrationNumber ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="e.g., ABC 123 GP"
                    />
                    {errors.registrationNumber && <p className="text-red-500 text-xs mt-1">{errors.registrationNumber}</p>}
                  </div>
                  
                  {/* Make */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Make</label>
                    <input
                      type="text"
                      name="make"
                      value={make}
                      onChange={handleInputChange}
                      className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.make ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="e.g., Jurgens"
                    />
                    {errors.make && <p className="text-red-500 text-xs mt-1">{errors.make}</p>}
                  </div>
                  
                  {/* Model */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Model</label>
                    <input
                      type="text"
                      name="model"
                      value={model}
                      onChange={handleInputChange}
                      className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.model ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="e.g., Exclusive"
                    />
                    {errors.model && <p className="text-red-500 text-xs mt-1">{errors.model}</p>}
                  </div>
                  
                  {/* Year */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Year</label>
                    <YearPicker
                      value={year ? parseInt(year) : undefined}
                      onChange={(selectedYear) => setYear(selectedYear.toString())}
                      disabled={false}
                    />
                  </div>
                  
                  {/* Cover Type */}
                  <div>
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Cover Type</label>
                    <select
                      name="coverType"
                      value={coverType}
                      onChange={handleInputChange}
                      className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.coverType ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="" disabled>Select Cover Type</option>
                      {coverTypes.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                    {errors.coverType && <p className="text-red-500 text-xs mt-1">{errors.coverType}</p>}
                  </div>
                </div>
                
                {/* Insured Value and No Claim Bonus */}
                <div className="flex space-x-6 mb-6">
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
  type="text"
  name="insuredValue"
  value={currencyDisplay.insuredValue}
  onChange={handleInputChange}
  onBlur={() => {
    const numericValue = parseCurrencyInput(currencyDisplay.insuredValue);
    if (numericValue > 0) {
      setCurrencyDisplay(prev => ({
        ...prev,
        insuredValue: formatCurrencyInput(numericValue.toFixed(2))
      }));
    }
  }}
  className={`shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.insuredValue ? 'border-red-500' : 'border-gray-300'}`}
  placeholder="0.00"
/>
                    </div>
                    {errors.insuredValue && <p className="text-red-500 text-xs mt-1">{errors.insuredValue}</p>}
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">No Claim Bonus</label>
                    <select
                      name="noClaimBonus"
                      value={noClaimBonus}
                      onChange={handleInputChange}
                      className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                    >
                      <option value="" disabled>Select bonus</option>
                      {noClaimBonusOptions.map(option => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </div>
                </div>
                
                {/* Use Retail Value Checkbox */}
                <div className="mt-4 mb-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useRetailValue"
                      name="useRetailValue"
                      checked={useRetailValue}
                      onChange={handleInputChange}
                      className="form-checkbox h-4 w-4 text-blue-600 rounded"
                    />
                    <label htmlFor="useRetailValue" className="ml-2 text-gray-700 text-sm">Use retail value</label>
                  </div>
                </div>
                
                {/* Premium and Excess */}
                <div className="flex space-x-6 mb-6">
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Premium</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        type="text"
                        name="premium"
                        value={currencyDisplay.premium}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        type="text"
                        name="excess"
                        value={currencyDisplay.excess}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">

                  <button
  type="button"
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=trailer&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=trailer&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg"
>
  Cancel
</button>

                  <button
                    type="submit"
                    disabled={isSaving}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-blue-300"
                  >
                    {isSaving ? 'Saving...' : (editingId ? 'Update Trailer/Caravan' : 'Add Trailer/Caravan')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTrailerCaravanPage;